import { useTheme } from '@mui/material/styles';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';

// project imports
import SubCard from 'ui-component/cards/SubCard';
import MainCard from 'ui-component/cards/MainCard';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { gridSpacing } from 'store/constant';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useScriptRef from 'hooks/useScriptRef';
import { openSnackbar } from 'store/slices/snackbar';
import axiosServices from 'utils/axios-services';
import { useEffect, useState } from 'react';
import { useDispatch } from 'store';
import { countries, Country, countryToFlag, getCountryFromCode } from 'utils/country';
import AppBar from 'ui-component/extended/AppBar';
import useAuth from 'hooks/useAuth';

const currencies = [
    {
        value: 'export',
        label: 'Export'
    },
    {
        value: 'technical',
        label: 'Technical'
    }
];

type Certificate = {
    usn: string;
    como: string[] | null;
    i_country: Country | null;
    aes: string;
    an: string;
    doi: Date;
    doe: Date;
    status: string;
    type: string;
    nop_10: number | null;
    carat_10: number | null;
    value_10: number | null;
    nop_21: number | null;
    carat_21: number | null;
    value_21: number | null;
    nop_31: number | null;
    carat_31: number | null;
    value_31: number | null;
};
const toIntString = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? String(Math.trunc(n)) : '';
};
const toIntStringFromApi = (v: unknown) => {
    if (v === null || v === undefined || v === '') return '';
    // strip any decimals that may arrive (e.g., "12.00" -> "12")
    const s = String(v).replace(/\..*$/, '');
    // keep only digits (safety)
    const digits = s.replace(/\D/g, '');
    return digits;
};
const initialValues = {
    usn: '',
    como: null as Country[] | null,
    i_country: null as Country | null,
    aes: '',
    an: '',
    doi: null as Date | null,
    doe: null as Date | null,

    status: '',
    type: '',
    submit: null,
    nop_10: '',
    nop_21: '',
    nop_31: ''
};

const validationSchema = yup.object({
    usn: yup.string().required('usn is required'),
    como: yup.array().of(yup.object()).min(1, 'At least one country of mining origin is required').nullable(),
    i_country: yup.object().required('i_country is required').nullable(),
    type: yup.string().required('Type is required'),
    aes: yup.string().required('aes is required'),
    an: yup.string().required('an is required'),
    doi: yup.date().required('doi is required').nullable(),
    doe: yup.date().required('doe is required').nullable()
});

export default function ViewCertificateSummary() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();
    const { id } = useParams();
    const scriptedRef = useScriptRef();
    const dispatch = useDispatch();

    const [openPermissionDialog, setOpenPermissionDialog] = useState(false);

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
                // setOpenIssueDialogIssueDialog(true);
                await axiosServices.put(`/certificates/${id}`, {
                    ...values,
                    como: values.como?.map((country) => country.code),
                    i_country: values.i_country?.code
                    // e_country: values.e_country?.label,
                    // i_country: values.i_country?.label
                });

                if (scriptedRef.current) {
                    setStatus({ success: true });
                    setSubmitting(false);
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: 'Certificate updated successfully.',
                            variant: 'alert',
                            alert: {
                                color: 'success'
                            },
                            close: false
                        })
                    );

                    // setTimeout(() => {
                    //     navigate('/certificates', { replace: true });
                    // }, 100);
                }
            } catch (err: any) {
                console.error(err);
                if (scriptedRef.current) {
                    setStatus({ success: false });
                    setErrors({ submit: err.message });
                    setSubmitting(false);
                }
            }
        }
    });

    const getCertificate = async () => {
        try {
            const response = await axiosServices.get(!isLoggedIn ? `/summary-certificate/${id}` : `certificates/${id}`, {
                params: {
                    id: id
                }
            });
            const certificateInfo: Certificate = response?.data;

            formik.setValues({
                usn: certificateInfo?.usn || '',
                como: (Array.isArray(certificateInfo.como) ? certificateInfo.como : JSON.parse(certificateInfo.como || '[]'))
                    .map((label: string) => getCountryFromCode(label))
                    .filter((country: Country): country is Country => country !== null),
                i_country: getCountryFromCode((certificateInfo?.i_country as unknown as string) || ''),
                aes: certificateInfo?.aes || '',
                an: certificateInfo?.an || '',
                doi: certificateInfo?.doi || null,
                doe: certificateInfo?.doe || null,
                status: certificateInfo?.status || '',
                type: certificateInfo?.type || '',
                nop_10: toIntStringFromApi(certificateInfo?.nop_10),
                nop_21: toIntStringFromApi(certificateInfo?.nop_21),
                nop_31: toIntStringFromApi(certificateInfo?.nop_31),
                submit: null
            });
        } catch (err: any) {
            console.log(err.error);
            if (err.code === 404) {
                setOpenPermissionDialog(true);
            }
        }
    };

    useEffect(() => {
        if (scriptedRef.current && id !== '' && id !== undefined) {
            getCertificate();
        }
    }, [id, scriptedRef]);

    const goDetails = () => {
        navigate(`/certificates/${id}/details`, { replace: true });
    };

    const onClose = () => {
        navigate(`/`, { replace: true });
    };

    return (
        <>
            <AppBar />
            <MainCard sx={{ ml: 2, mr: 2, mt: 10 }}>
                <Grid sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt: 2 }}>
                    <Typography variant="h1">Public Certificate Details</Typography>
                </Grid>
                <form onSubmit={formik.handleSubmit}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12}>
                                <SubCard title="Basic Infomation">
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Unique Serial ID"
                                                name="usn"
                                                value={formik.values.usn}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.usn && Boolean(formik.errors.usn)}
                                                // helperText={formik.touched.usn && formik.errors.usn}
                                                aria-readonly
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Status"
                                                value={formik.values.status}
                                                aria-readonly
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField
                                                id="outlined-select-currency"
                                                select
                                                label="Type"
                                                fullWidth
                                                name="type"
                                                value={formik.values.type}
                                                // onChange={formik.handleChange}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            >
                                                {currencies.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Autocomplete
                                                id="country-select-demo"
                                                value={formik.values.como || []}
                                                onChange={(_, value) => formik.setFieldValue('como', value as Country[])}
                                                options={countries}
                                                autoHighlight
                                                multiple
                                                disableClearable
                                                readOnly
                                                getOptionLabel={(option) => option.label}
                                                renderOption={(props, option) => (
                                                    <li {...props} style={{ fontSize: 15 }}>
                                                        <span style={{ marginRight: 10, fontSize: 18 }}>{countryToFlag(option.code)}</span>
                                                        {option.label} ({option.code}) +{option.phone}
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        name="como"
                                                        error={formik.touched.como && Boolean(formik.errors.como)}
                                                        // helperText={formik.touched.como && formik.errors.como}
                                                        {...params}
                                                        label="Country of Mining Origin"
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            autoComplete: 'new-password' // disable autocomplete and autofill
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Autocomplete
                                                id="i_country"
                                                options={countries}
                                                autoHighlight
                                                value={formik.values.i_country}
                                                onChange={(_, value) => formik.setFieldValue('i_country', value)}
                                                getOptionLabel={(option) => option.label}
                                                sx={{ mt: 4.5 }}
                                                readOnly
                                                renderOption={(props, option) => (
                                                    <Box component="li" sx={{ '& > img': { mr: 1, flexShrink: 0 } }} {...props}>
                                                        <li {...props} style={{ fontSize: 15 }}>
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                                style={{ marginRight: 10, fontSize: 18 }}
                                                            />
                                                            {option.label} ({option.code})
                                                        </li>
                                                    </Box>
                                                )}
                                                renderInput={(params) => {
                                                    const selectedCountry = formik.values.i_country;

                                                    return (
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                '.MuiAutocomplete-clearIndicator': { display: 'none' },
                                                                mt: selectedCountry?.code ? -2.3 : -1.2
                                                            }}
                                                        >
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                style={{
                                                                    marginLeft: '10px',
                                                                    marginRight: 10,
                                                                    zIndex: 9
                                                                }}
                                                                src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${selectedCountry?.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                            />
                                                            <TextField
                                                                name="i_country"
                                                                error={formik.touched.i_country && Boolean(formik.errors.i_country)}
                                                                helperText={formik.touched.i_country && formik.errors.i_country}
                                                                {...params}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    '.MuiFilledInput-root': { border: 'none' },
                                                                    '& input': { marginLeft: '30px' }
                                                                }}
                                                                label="Issuing Country"
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: 'new-password' // disable autocomplete and autofill
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>

                            {/* <Grid item xs={12} md={6}>
                                <SubCard title="Exporter">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Name"
                                                name="e_name"
                                                value={formik.values.e_name}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_name && Boolean(formik.errors.e_name)}
                                                // helperText={formik.touched.e_name && formik.errors.e_name}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                id="country-select-demo"
                                                options={countries}
                                                autoHighlight
                                                value={formik.values.e_country}
                                                onChange={(_, value) => formik.setFieldValue('e_country', value)}
                                                getOptionLabel={(option) => option.label}
                                                readOnly
                                                renderOption={(props, option) => (
                                                    <Box component="li" sx={{ '& > img': { mr: 1, flexShrink: 0 } }} {...props}>
                                                        <li {...props} style={{ fontSize: 15 }}>
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                                style={{ marginRight: 10, fontSize: 18 }}
                                                            />
                                                            {option.label} ({option.code})
                                                        </li>
                                                    </Box>
                                                )}
                                                renderInput={(params) => {
                                                    const selectedCountry = formik.values.e_country;
                                                    return (
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                '.MuiAutocomplete-clearIndicator': { display: 'none' },
                                                                mt: selectedCountry?.code ? -2.3 : -1.2
                                                            }}
                                                        >
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                style={{
                                                                    marginLeft: '10px',
                                                                    marginRight: 10,
                                                                    zIndex: 9
                                                                }}
                                                                src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${selectedCountry?.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                            />
                                                            <TextField
                                                                name="e_country"
                                                                error={formik.touched.e_country && Boolean(formik.errors.e_country)}
                                                                helperText={formik.touched.e_country && formik.errors.e_country}
                                                                {...params}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    '.MuiFilledInput-root': { border: 'none' },
                                                                    '& input': { marginLeft: '30px' }
                                                                }}
                                                                label="Country"
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: 'new-password' // disable autocomplete and autofill
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                }}
                                                sx={{ mt: 4.5, mb: 3.4 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Address Line1"
                                                name="e_addr1"
                                                value={formik.values.e_addr1}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_addr1 && Boolean(formik.errors.e_addr1)}
                                                // helperText={formik.touched.e_addr1 && formik.errors.e_addr1}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Address Line2"
                                                name="e_addr2"
                                                value={formik.values.e_addr2}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_addr2 && Boolean(formik.errors.e_addr2)}
                                                // helperText={formik.touched.e_addr2 && formik.errors.e_addr2}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Town/City"
                                                name="e_city"
                                                value={formik.values.e_city}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_city && Boolean(formik.errors.e_city)}
                                                // helperText={formik.touched.e_city && formik.errors.e_city}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="State"
                                                name="e_state"
                                                value={formik.values.e_state}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_state && Boolean(formik.errors.e_state)}
                                                // helperText={formik.touched.e_state && formik.errors.e_state}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="ZIP/Post Code"
                                                name="e_zip"
                                                value={formik.values.e_zip}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.e_zip && Boolean(formik.errors.e_zip)}
                                                // helperText={formik.touched.e_zip && formik.errors.e_zip}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <SubCard title="Importer">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Name"
                                                name="i_name"
                                                value={formik.values.i_name}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_name && Boolean(formik.errors.i_name)}
                                                // helperText={formik.touched.i_name && formik.errors.i_name}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                id="country-select-demo"
                                                options={countries}
                                                autoHighlight
                                                value={formik.values.i_country}
                                                onChange={(_, value) => formik.setFieldValue('i_country', value)}
                                                getOptionLabel={(option) => option.label}
                                                readOnly
                                                renderOption={(props, option) => (
                                                    <Box component="li" sx={{ '& > img': { mr: 1, flexShrink: 0 } }} {...props}>
                                                        <li {...props} style={{ fontSize: 15 }}>
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                                style={{ marginRight: 10, fontSize: 18 }}
                                                            />
                                                            {option.label} ({option.code})
                                                        </li>
                                                    </Box>
                                                )}
                                                renderInput={(params) => {
                                                    const selectedCountry = formik.values.i_country;
                                                    return (
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                '.MuiAutocomplete-clearIndicator': { display: 'none' },
                                                                mt: selectedCountry?.code ? -2.3 : -1.2
                                                            }}
                                                        >
                                                            <img
                                                                loading="lazy"
                                                                width="25"
                                                                style={{
                                                                    marginLeft: '10px',
                                                                    marginRight: 10,
                                                                    zIndex: 9
                                                                }}
                                                                src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`}
                                                                srcSet={`https://flagcdn.com/w40/${selectedCountry?.code.toLowerCase()}.png 2x`}
                                                                alt=""
                                                            />
                                                            <TextField
                                                                name="i_country"
                                                                error={formik.touched.i_country && Boolean(formik.errors.i_country)}
                                                                helperText={formik.touched.i_country && formik.errors.i_country}
                                                                {...params}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    '.MuiFilledInput-root': { border: 'none' },
                                                                    '& input': { marginLeft: '30px' }
                                                                }}
                                                                label="Country"
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: 'new-password' // disable autocomplete and autofill
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                }}
                                                sx={{ mt: 4.5, mb: 3.4 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Address Line1"
                                                name="i_addr1"
                                                value={formik.values.i_addr1}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_addr1 && Boolean(formik.errors.i_addr1)}
                                                // helperText={formik.touched.i_addr1 && formik.errors.i_addr1}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Address Line2"
                                                name="i_addr2"
                                                value={formik.values.i_addr2}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_addr2 && Boolean(formik.errors.i_addr2)}
                                                // helperText={formik.touched.i_addr2 && formik.errors.i_addr2}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Town/City"
                                                name="i_city"
                                                value={formik.values.i_city}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_city && Boolean(formik.errors.i_city)}
                                                // helperText={formik.touched.i_city && formik.errors.i_city}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="State"
                                                name="i_state"
                                                value={formik.values.i_state}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_state && Boolean(formik.errors.i_state)}
                                                // helperText={formik.touched.i_state && formik.errors.i_state}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="ZIP/Post Code"
                                                name="i_zip"
                                                value={formik.values.i_zip}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.i_zip && Boolean(formik.errors.i_zip)}
                                                // helperText={formik.touched.i_zip && formik.errors.i_zip}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid> */}

                            {/* <Grid item xs={12}>
                                <SubCard title="Certification Details">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sx={{ display: 'flex' }}>
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle1">HS CODE</Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle1">No. Parcels</Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle1">Total Caratage</Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Typography variant="subtitle1">USD Value</Typography>
                                            </Grid>
                                        </Grid>
                                        <Divider
                                            flexItem
                                            sx={{ width: '100%', ml: 1, mt: 1, border: `1px solid ${theme.palette.primary}` }}
                                        />
                                        <Grid item xs={12} sx={{ display: 'flex' }} container spacing={2}>
                                            <Grid item xs={3} sx={{ ml: -1, display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body1" sx={{ ml: 1 }}>
                                                    7102.10
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="nop_10"
                                                    value={formik.values.nop_10}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.nop_10 && Boolean(formik.errors.nop_10)}
                                                    // helperText={formik.touched.nop_10 && formik.errors.nop_10}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="carat_10"
                                                    value={formik.values.carat_10}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.carat_10 && Boolean(formik.errors.carat_10)}
                                                    // helperText={formik.touched.carat_10 && formik.errors.carat_10}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="value_10"
                                                    value={formik.values.value_10}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.value_10 && Boolean(formik.errors.value_10)}
                                                    // helperText={formik.touched.value_10 && formik.errors.value_10}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sx={{ display: 'flex' }} container spacing={2}>
                                            <Grid item xs={3} sx={{ ml: -1, display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body1" sx={{ ml: 1 }}>
                                                    7102.21
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="nop_21"
                                                    value={formik.values.nop_21}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.nop_21 && Boolean(formik.errors.nop_21)}
                                                    // helperText={formik.touched.nop_21 && formik.errors.nop_21}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="carat_21"
                                                    value={formik.values.carat_21}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.carat_21 && Boolean(formik.errors.carat_21)}
                                                    // helperText={formik.touched.carat_21 && formik.errors.carat_21}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="value_21"
                                                    value={formik.values.value_21}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.value_21 && Boolean(formik.errors.value_21)}
                                                    // helperText={formik.touched.value_21 && formik.errors.value_21}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sx={{ display: 'flex' }} container spacing={2}>
                                            <Grid item xs={3} sx={{ ml: -1, display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body1" sx={{ ml: 1 }}>
                                                    7102.31
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="nop_31"
                                                    value={formik.values.nop_31}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.nop_31 && Boolean(formik.errors.nop_31)}
                                                    // helperText={formik.touched.nop_31 && formik.errors.nop_31}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="carat_31"
                                                    value={formik.values.carat_31}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.carat_31 && Boolean(formik.errors.carat_31)}
                                                    // helperText={formik.touched.carat_31 && formik.errors.carat_31}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3} sx={{ ml: 0 }}>
                                                <TextField
                                                    fullWidth
                                                    id="outlined-basic"
                                                    label=""
                                                    name="value_31"
                                                    value={formik.values.value_31}
                                                    // onChange={formik.handleChange}
                                                    // error={formik.touched.value_31 && Boolean(formik.errors.value_31)}
                                                    // helperText={formik.touched.value_31 && formik.errors.value_31}
                                                    InputProps={{
                                                        readOnly: true
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid> */}
                            <Grid item xs={12}>
                                <SubCard title="Others Infomation">
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Authorized eSignature"
                                                value={formik.values.aes}
                                                disabled
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <DatePicker
                                                label="Date of Issuance"
                                                value={formik.values.doi}
                                                onChange={(newValue) => formik.setFieldValue('doi', newValue)}
                                                readOnly
                                                renderInput={(params) => <TextField {...params} fullWidth name="doi" />}
                                                inputFormat="MM-dd-yyyy"
                                                mask="__-__-____"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                id="outlined-basic"
                                                label="Authorized Name"
                                                name="an"
                                                value={formik.values.an}
                                                // onChange={formik.handleChange}
                                                // error={formik.touched.an && Boolean(formik.errors.an)}
                                                // helperText={formik.touched.an && formik.errors.an}
                                                InputProps={{
                                                    readOnly: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <DatePicker
                                                label="Date of Expiry"
                                                value={formik.values.doe}
                                                onChange={(_newValue) => {
                                                    formik.setFieldValue('doe', formik.values.doe);
                                                }}
                                                inputFormat="MM-dd-yyyy"
                                                // mask="__-__-____"
                                                readOnly
                                                renderInput={(params) => <TextField {...params} fullWidth name="doe" />}
                                            />
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                    <AnimateButton>
                                        <Button variant="contained" size="large" onClick={() => goDetails()}>
                                            Login to view more details
                                        </Button>
                                    </AnimateButton>
                                </Stack>
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </form>
                <Dialog
                    open={openPermissionDialog}
                    onClose={onClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    sx={{ p: 3 }}
                    disableEscapeKeyDown
                >
                    <DialogTitle id="alert-dialog-title" sx={{ mt: 2, mb: 2 }}>
                        Can not find your certificate.
                        <br />
                        {`Please check again your certificate UUID - ${id}.`}
                    </DialogTitle>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button variant="outlined" sx={{ minWidth: '150px' }} onClick={() => onClose()}>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </MainCard>
        </>
    );
}
